// Currency column for Target Value that additionally pins unset (blank) values to the bottom in BOTH sort
// directions. The design-system CurrencyColumn renders the value (a dash for a missing one) and formats the
// currency, but its column definition does not carry `sortUndefined`, so a blank Target Value sorts on its
// comparator value (0) — the bottom when descending but the top when ascending. We reuse the DS
// CurrencyColumn's column definition verbatim and re-attach `sortUndefined` so rows whose sorted value is
// `undefined` (a blank / $0 target) sort last regardless of direction, matching the Win Probability column.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import type {IntlShape} from 'react-intl';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import CurrencyColumn from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/CurrencyColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import type {CurrencyColumnType} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/CurrencyColumn';
import type {OpportunityTableRow} from '../opportunityTableRows';

export interface TargetValueColumnType extends CurrencyColumnType {
	// Placement for rows whose sorted value (accessor) is `undefined`, passed straight to the tanstack column
	// (the DS CurrencyColumn drops it). 'first' / 'last' pin unset rows regardless of sort direction;
	// `false` disables table-core's undefined pre-pass entirely so the column's own comparator decides.
	//
	// That pre-pass matters more than it looks: table-core's getDefaultColumnDef() sets `sortUndefined: 1`,
	// and getSortedRowModel resolves undefined accessor values BEFORE ever calling sortingFn, so a column
	// that simply omits this never sees its blanks. This file always writes the key, so an omitted prop
	// still overrides that default — but relying on that is invisible, hence `false` at the call site.
	sortUndefined?: 'first' | 'last' | false;
	// Wraps the DS-rendered currency value (the call site supplies the EditableCell). D14: the display
	// formatting itself is unchanged - this only wraps what CurrencyColumn already renders.
	cellContent: (content: React.ReactNode, row: OpportunityTableRow) => React.ReactNode;
}

// Config-only component: the DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TargetValueColumn = (_: TargetValueColumnType): null => null;

// Returns ColumnDef<any> to match the DS CurrencyColumn.toColumnDef it reuses (its accessorKey is typed
// loosely, so a narrower row-typed ColumnDef would not accept the spread).
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the DS CurrencyColumn.toColumnDef return type
TargetValueColumn.toColumnDef = (child: React.ReactElement<TargetValueColumnType>, intl: IntlShape): ColumnDef<any> => {
	const base = CurrencyColumn.toColumnDef(child, intl);
	return {
		...base,
		sortUndefined: child.props.sortUndefined,
		// The DS cell renders the whole StyledCell - the <td> - around its formatted value. Wrap what is
		// INSIDE it, not the cell itself: wrapping the outside put a <td> inside our trigger element,
		// which drops the column's alignment and made the markup differ depending on whether the user
		// could edit. Every other column here hands cellContent the value and keeps its own StyledCell.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the DS cell context is untyped here
		cell: (context: any) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the DS cell renderer is untyped
			const rendered = base.cell ? (base.cell as any)(context) : null;
			if (!React.isValidElement(rendered)) {
				return child.props.cellContent(rendered, context.row.original);
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the DS cell element is untyped
			const cellChildren = (rendered.props as any).children;
			return React.cloneElement(rendered, undefined, child.props.cellContent(cellChildren, context.row.original));
		},
	};
};

export default TargetValueColumn;
